import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/AddDiscount.css';

interface User {
  _id: string;
  name: string;
  email: string;
}

interface Product {
  _id: string;
  fieldData: {
    name: string;
  };
}

interface AddDiscountFormData {
  userId: string;
  product: string;
  discount: number;
  allProducts?: boolean;
}

const AddDiscount: React.FC = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<AddDiscountFormData>({
    userId: '',
    product: '',
    discount: 0,
    allProducts: false
  });
  
  // Add a separate state for the discount input value
  const [discountInput, setDiscountInput] = useState('');

  // Fetch all users for the dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      if (!token) return;
      
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/all`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }
        
        const data = await response.json();
        setUsers(data);
      } catch (err) {
        console.error('Failed to fetch users:', err);
        setError('Failed to load users. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, [token]);

  // Fetch all products for the dropdown
  useEffect(() => {
    const fetchProducts = async () => {
      if (!token) return;
      
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/products`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }
        
        const data = await response.json();
        setProducts(data);
      } catch (err) {
        console.error('Failed to fetch products:', err);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, [token]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ 
        ...prev, 
        [name]: checked,
        // Clear product selection when checking "all products"
        product: checked ? '' : prev.product 
      }));
    } else if (name === 'discount') {
      setDiscountInput(value);
      
      if (value === '') {
        setFormData({ ...formData, [name]: 0 });
      } else {
        const numValue = parseInt(value);
        if (!isNaN(numValue)) {
          const constrainedValue = Math.min(Math.max(numValue, 0), 100);
          setFormData({ ...formData, [name]: constrainedValue });
        }
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.userId) {
      setError('Please select a user');
      return;
    }
    
    if (!formData.allProducts && !formData.product) {
      setError('Please select a product or check "All Products"');
      return;
    }
    
    if (formData.discount < 0 || formData.discount > 100) {
      setError('Discount must be between 0% and 100%');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Prepare the request body
      const requestBody = {
        userId: formData.userId,
        discount: formData.discount,
        ...(formData.allProducts 
          ? { allProducts: true } 
          : { product: formData.product }
        )
      };
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/discounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Use the error message from the API response if available
        throw new Error(data.message || data.error || 'Failed to add discount');
      }
      
      // Clear form and show success message
      setFormData({
        userId: '',
        product: '',
        discount: 0,
        allProducts: false
      });
      setDiscountInput('');
      setSuccess('Discount added successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error('Failed to add discount:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-discount-container">
      <div className="discount-form-card">
        <h2 className="form-title">Add New Discount</h2>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        <form onSubmit={handleSubmit} className="discount-form">
          <div className="form-group">
            <label htmlFor="userId">Select User</label>
            <div className="select-wrapper">
              <select 
                id="userId" 
                name="userId" 
                value={formData.userId} 
                onChange={handleInputChange}
                disabled={loading}
                className="form-control"
              >
                <option value="">-- Select a user --</option>
                {users.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.name + " - " + user.email}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="product">Select Product</label>
            <div className="product-selection-wrapper">
              <div className="select-wrapper">
                <select 
                  id="product" 
                  name="product" 
                  value={formData.product} 
                  onChange={handleInputChange}
                  disabled={loading || formData.allProducts}
                  className="form-control"
                >
                  <option value="">-- Select a product --</option>
                  {products.map(product => (
                    <option key={product._id} value={product.fieldData.name}>
                      {product.fieldData.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="allProducts"
                  checked={formData.allProducts}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                All Products
              </label>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="discount">Discount Percentage (0-100%)</label>
            <div className="discount-input-wrapper">
              <input 
                type="number" 
                id="discount" 
                name="discount" 
                value={discountInput} 
                onChange={handleInputChange}
                min="0"
                max="100"
                disabled={loading}
                className="form-control"
                placeholder="Enter discount percentage"
              />
              <span className="percentage-symbol">%</span>
            </div>
          </div>
          
          <button 
            type="submit" 
            className="add-discount-button"
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add Discount'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddDiscount; 